import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { defaultSafecareConfig } from '../safecare/model/policyConfig';
import { runScenario, getScenarioSummary } from '../safecare/model/runScenario';
import { getScenarioById } from '../safecare/model/scenarioRegistry';
import { BaselineData } from '../safecare/data/baseline';
import { getBacktestComparisons } from '../safecare/model/historicalBenchmarks';
import { buildCalibrationReport } from '../safecare/model/calibrationReport';
import { runMonteCarlo } from '../safecare/model/monteCarlo';
import PolicyControls from '../safecare/components/PolicyControls';
import OutcomeDashboard from '../safecare/components/OutcomeDashboard';
import EventManager from '../safecare/components/EventManager';
import SimulatorGuide from '../safecare/components/SimulatorGuide';
import DiagnosticPanel from '../safecare/components/DiagnosticPanel';
import { parseMarkdownItalics } from '../components/SafecareBrand';
import Header from '../components/layout/Header';
import StateSelector from '../safecare/components/StateSelector';
import { useStateSimulation } from '../safecare/model/useStateSimulation';
import './SafecareSimulator.css';

/**
 * SAFECARE Policy Simulator Page
 * Main page component for the interactive policy simulator
 */
export default function SafecareSimulator() {
    const [selectedState, setSelectedState] = useState(null);
    const [config, setConfig] = useState(defaultSafecareConfig);
    const [eventsState, setEventsState] = useState({});
    const [activeScenario, setActiveScenario] = useState('default');
    const [isCalculating, setIsCalculating] = useState(false);
    const [isGuideOpen, setIsGuideOpen] = useState(false);
    const [uncertaintyResult, setUncertaintyResult] = useState(null);
    const [isRunningUncertainty, setIsRunningUncertainty] = useState(false);
    const [projectionTimeline, setProjectionTimeline] = useState(null);

    // Load projection_timeline.json from pipeline (map-hcs)
    useEffect(() => {
        fetch('/data/projection_timeline.json')
            .then((res) => (res.ok ? res.json() : null))
            .then((data) => setProjectionTimeline(data))
            .catch(() => setProjectionTimeline(null));
    }, []);

    // State-Level Simulation Hook
    const stateSimulation = useStateSimulation(selectedState, config, eventsState);

    // Toggle or Update event handler
    const handleEventToggle = (eventId, updates = null) => {
        // If we manually toggle an event, we are no longer in a preset
        setActiveScenario('custom');
        setEventsState(prev => {
            const currentState = prev[eventId] || { enabled: false };

            // Case 1: Just toggling (no updates)
            if (!updates) {
                return {
                    ...prev,
                    [eventId]: {
                        ...currentState,
                        enabled: !currentState.enabled
                    }
                };
            }

            // Case 2: Updating specific fields (enabled status implicit if not provided, or assumed true if updating props)
            return {
                ...prev,
                [eventId]: {
                    ...currentState,
                    ...updates,
                    enabled: true // Auto-enable if adjusting settings
                }
            };
        });
    };

    const handleConfigChange = (newConfig) => {
        setActiveScenario('custom');
        setConfig(newConfig);
    };

    // Handler: Apply Scenario (Toggle Logic)
    const handleApplyScenario = (scenarioId) => {
        // Toggle off if already active
        if (activeScenario === scenarioId) {
            handleResetAll();
            return;
        }

        const scenario = getScenarioById(scenarioId);
        if (!scenario) return;

        setActiveScenario(scenario.id);

        // 1. Apply Config Overrides
        const newConfig = {
            ...defaultSafecareConfig,
            ...scenario.configOverrides,
        };
        setConfig(newConfig);

        // 2. Apply Event Overrides
        setEventsState(scenario.eventOverrides || {});
    };

    const handleResetAll = () => {
        setActiveScenario('default');
        setConfig(defaultSafecareConfig);
        setEventsState({});
    };

    // Run scenario whenever config or events change (DUAL PASS: Baseline + Scenario)
    const results = useMemo(() => {
        // Determine Structural Overrides based on Active Scenario
        let structuralOverrides = null;
        if (activeScenario !== 'custom' && activeScenario !== 'default') {
            const scenario = getScenarioById(activeScenario);
            if (scenario) {
                structuralOverrides = scenario.structuralOverrides;
            }
        }

        return runScenario(config, eventsState, structuralOverrides, projectionTimeline);
    }, [config, eventsState, activeScenario, projectionTimeline]);

    const summary = getScenarioSummary(results);

    // Rigor data: backtest + calibration (stable, computed once per session or on demand)
    const rigorData = useMemo(() => ({
        backtest: getBacktestComparisons(),
        calibrationReport: buildCalibrationReport()
    }), []);

    const handleRunUncertainty = useCallback(() => {
        setIsRunningUncertainty(true);
        setUncertaintyResult(null);
        const enabledIds = typeof eventsState === 'object' && !Array.isArray(eventsState)
            ? Object.keys(eventsState).filter(k => eventsState[k]?.enabled)
            : eventsState;
        requestAnimationFrame(() => {
            try {
                const mc = runMonteCarlo(config, { runs: 50, enabledEventIds: enabledIds });
                setUncertaintyResult(mc);
            } catch (e) {
                console.error('Monte Carlo run failed:', e);
            } finally {
                setIsRunningUncertainty(false);
            }
        });
    }, [config, eventsState]);

    return (
        <div className="safecare-simulator-page">
            <Header />
            {/* Compact Dashboard Header */}
            <header className="dashboard-header">
                <div className="header-left">
                    <h1>{parseMarkdownItalics("SAFEC+")} Policy Simulator</h1>
                    <span className="header-badge">FRAMEWORK v1.0</span>
                    {results?.scenarioResults?.some(y => y.spendingSource === 'pipeline') && (
                        <span className="header-badge data-pipeline" title="Spending from map-hcs pipeline">Pipeline</span>
                    )}
                    <button
                        className="help-btn"
                        onClick={() => setIsGuideOpen(true)}
                        title="Policy Simulator - SAFEC+"
                    >
                        ? Help
                    </button>
                </div>

                {summary && (
                    <div className="header-stats">
                        <div className="stat-item">
                            <span className="stat-label">Health Spending</span>
                            <span className="stat-value positive">{summary.healthSpendingReduction.firstYear}</span>
                        </div>
                        <div className="header-divider"></div>
                        <div className="stat-item">
                            <span className="stat-label">Household Savings</span>
                            <span className="stat-value highlight">{summary.householdSavings.avgPerPerson}/yr</span>
                        </div>
                        <div className="header-divider"></div>
                        <div className="stat-item">
                            <span className="stat-label">Debt Erased</span>
                            <span className="stat-value stat-value-positive">{summary.medicalDebt.eliminated}</span>
                        </div>
                    </div>
                )}
            </header>

            {/* Main Workspace (Sidebar + Dashboard) */}
            <div className="simulator-workspace">
                {/* Left: Policy Controls */}
                <aside className="controls-sidebar">
                    <div className="sidebar-scroll">
                        <StateSelector
                            selectedState={selectedState}
                            onSelect={setSelectedState}
                        />

                        <PolicyControls
                            config={config}
                            onChange={handleConfigChange}
                            activeScenario={activeScenario}
                            onApplyScenario={handleApplyScenario}
                            onReset={handleResetAll}
                            />

                        {/* Dynamic Events Panel */}
                        <EventManager
                            eventsState={eventsState} // Renamed prop
                            onToggleEvent={handleEventToggle}
                        />

                        {/* Info / Footer in Sidebar */}
                        <div className="sidebar-footer">
                            <h3>About This Simulator</h3>
                            <p>
                                Model impact of SAFECARE Act policies on spending, debt, and costs.
                                Data based on CMS & CBO baselines.
                            </p>
                        </div>
                    </div>
                </aside>

                {/* Right: Outcome Dashboard */}
                <main className="dashboard-main">
                    <div className="dashboard-scroll">
                        {isCalculating && (
                            <div className="calculating-overlay">
                                <div className="calculating-spinner"></div>
                                <p>Updating Projection...</p>
                            </div>
                        )}
                        <OutcomeDashboard
                            results={results}
                            stateResults={stateSimulation.results}
                            selectedState={selectedState}
                            config={config}
                            baselineData={BaselineData}
                            onUpdateConfig={handleConfigChange}
                        />
                    </div>
                </main>
            </div>

            {/* Global Diagnostic Panel (Fixed Position) */}
            <DiagnosticPanel
                config={config}
                results={results}
                baselineData={BaselineData}
                rigorData={rigorData}
                uncertaintyResult={uncertaintyResult}
                onRunUncertainty={handleRunUncertainty}
                isRunningUncertainty={isRunningUncertainty}
            />

            <SimulatorGuide
                isOpen={isGuideOpen}
                onClose={() => setIsGuideOpen(false)}
            />
        </div>
    );
}
